import { createSlice } from '@reduxjs/toolkit';

/**
 * Estado de la bandeja de notificaciones (campana + pushes en vivo).
 *
 * - `unreadCount`: la fuente del badge de la campana.
 * - `items`: lo que pinta el dropdown cuando se abre.
 * - `queue`: pushes pendientes de mostrarse como modal (uno a la vez).
 *
 * Fuera de `redux-persist` a propósito (mismo criterio que `realtimeSlice`):
 * una recarga arranca en cero y el `notifications.snapshot` del WebSocket lo
 * repara con el contador real de la base.
 */
const initialState = {
  items: [],
  unreadCount: 0,
  queue: [],
  fetched: false,
  detalle: null,
};

const notificationsSlice = createSlice({
  name: 'notifications',
  initialState,
  reducers: {
    // Contador que llega al conectar: lo no leído que quedó en la base.
    snapshotReceived: (state, { payload }) => {
      state.unreadCount = payload.unreadCount || 0;
    },

    // Notificación en vivo (`notification.new`): ya está persistida en el
    // backend, aquí solo se refleja y se encola el modal tipo push.
    notificationReceived: (state, { payload }) => {
      const existente = state.items.find((item) => item.id === payload.id);
      if (existente) {
        // Repetida: se actualiza sin duplicar ítem, contador ni push.
        Object.assign(existente, payload);
        state.items = [
          payload,
          ...state.items.filter((item) => item.id !== payload.id),
        ];
        return;
      }
      state.items.unshift(payload);
      state.unreadCount += 1;
      state.queue.push(payload);
    },

    // Verdad del servidor al abrir la campana: reemplaza el listado local.
    setItems: (state, { payload }) => {
      state.items = payload.results || [];
      state.unreadCount = payload.unreadCount || 0;
      state.fetched = true;
    },

    // `{ id, readAt }` — solo la primera lectura baja el contador.
    markRead: (state, { payload }) => {
      const notificacion = state.items.find((item) => item.id === payload.id);
      if (!notificacion || notificacion.read_at) return;
      notificacion.read_at = payload.readAt;
      state.unreadCount = Math.max(0, state.unreadCount - 1);
    },

    // payload: fecha de lectura para las que aún no la tienen.
    markAllRead: (state, { payload }) => {
      state.items.forEach((item) => {
        if (!item.read_at) item.read_at = payload;
      });
      state.unreadCount = 0;
    },

    // El modal mostró (o descartó) el primer push de la cola.
    dismissPush: (state) => {
      state.queue.shift();
    },

    // Notificación abierta en el modal de detalle (clic en el dropdown o en
    // un push de anuncio). Los anuncios no navegan: muestran todo su detalle
    // aquí, incluidas imágenes cuando el backend las envíe.
    openDetail: (state, { payload }) => {
      state.detalle = payload;
    },

    closeDetail: (state) => {
      state.detalle = null;
    },
  },
});

export const {
  snapshotReceived,
  notificationReceived,
  setItems,
  markRead,
  markAllRead,
  dismissPush,
  openDetail,
  closeDetail,
} = notificationsSlice.actions;

export default notificationsSlice.reducer;
